import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Divider,
    Chip,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchNodeSchedule } from "../../../redux/actions/scheduleActions";
import {
    selectSnapshot,
    selectLoading,
    selectError,
    NodeSchedule,
    RichSession,
} from "../../../redux/reducers/scheduleReducer";
import { AppDispatch } from "../../../redux/store";
import { format } from "date-fns";
import { getSessionStatusColor } from "./utils";

/**
 * Format duration in seconds to a human-readable string
 */
const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

    return parts.join(" ");
};

const SessionScheduleView: React.FC = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const snapshot = useSelector(selectSnapshot);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    const [session, setSession] = useState<RichSession | null>(null);
    const [parentNode, setParentNode] = useState<NodeSchedule | null>(null);

    useEffect(() => {
        if (!snapshot) {
            dispatch(fetchNodeSchedule(false));
        }
    }, [dispatch, snapshot]);

    useEffect(() => {
        if (!snapshot || !sessionId) return;

        for (const node of snapshot.node_schedules) {
            for (const s of node.sessions) {
                if (s.s?.session_id === sessionId) {
                    setSession(s);
                    setParentNode(node);
                    return;
                }
            }
        }
    }, [snapshot, sessionId]);

    if (loading && !session) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!session) return <Alert severity="warning">Session not found.</Alert>;

    const apiSession = session.s;

    return (
        <Box sx={{ p: 4 }}>
            <ToastContainer position="top-center" autoClose={3000} />

            <Typography variant="h5" gutterBottom>
                Session Details
            </Typography>

            <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
                <Typography variant="body1" gutterBottom>
                    <strong>Session ID:</strong> {apiSession?.session_id}
                </Typography>
                <Typography variant="body1" gutterBottom>
                    <strong>Deployment ID:</strong> {apiSession?.deployment_id}
                </Typography>
                <Typography variant="body1" gutterBottom>
                    <strong>Organization ID:</strong> {apiSession?.organization_id}
                </Typography>
                
                {/* User Email */}
                {apiSession?.user_email && (
                    <Typography variant="body1" gutterBottom>
                        <strong>User Email:</strong> {apiSession.user_email}
                    </Typography>
                )}
                
                {/* Status with colored chip */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography variant="body1">
                        <strong>Status:</strong>
                    </Typography>
                    {apiSession?.status && (
                        <Chip
                            label={apiSession.status}
                            size="small"
                            sx={{
                                backgroundColor: getSessionStatusColor(apiSession.status),
                                color: "#fff",
                                fontWeight: 500,
                            }}
                        />
                    )}
                </Box>
                <Typography variant="body1" gutterBottom>
                    <strong>Created At:</strong>{" "}
                    {apiSession?.created_at
                        ? format(new Date(apiSession.created_at), "yyyy-MM-dd HH:mm:ss")
                        : "-"}
                </Typography>
                <Typography variant="body1" gutterBottom>
                    <strong>Expires At:</strong>{" "}
                    {apiSession?.expire_after
                        ? format(new Date(apiSession.expire_after), "yyyy-MM-dd HH:mm:ss")
                        : "-"}
                </Typography>

                {/* Phantom Session Lifetime Info */}
                {session.launch_time && (
                    <>
                        <Typography variant="body1" gutterBottom>
                            <strong>Launch Time:</strong>{" "}
                            {format(new Date(session.launch_time), "yyyy-MM-dd HH:mm:ss")}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            <strong>Session Lifetime:</strong>{" "}
                            {formatDuration(
                                (new Date().getTime() - new Date(session.launch_time).getTime()) / 1000
                            )}
                            {session.is_phantom && (
                                <Chip
                                    label="PHANTOM"
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        backgroundColor: "#9c27b0",
                                        color: "#fff",
                                        fontWeight: 500,
                                    }}
                                />
                            )}
                        </Typography>
                    </>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="body1" gutterBottom>
                    <strong>Node:</strong> {parentNode?.node_id || "unknown"} ({parentNode?.node_type})
                </Typography>

                {apiSession?.conversation_id && (
                    <Button
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onClick={() => navigate(`/dashboard/conversations/${apiSession.conversation_id}`)}
                    >
                        Go to Conversation
                    </Button>
                )}
            </Paper>

            <Button
                variant="contained"
                sx={{ mt: 3 }}
                onClick={() => navigate("/dashboard/session-schedule")}
            >
                Back to Schedule
            </Button>
        </Box>
    );
};

export default SessionScheduleView;
